/**
 * Modelfilter voor Mass Messages > Overview (24-10-2026, Jordy).
 *
 * Keuzelijst met zoeken en meerdere keuzes, plus "All models". De keuzes komen
 * alleen uit de rijen die de gebruiker al ziet (de server filtert die al op zijn
 * rechten), dus het filter toont nooit een model dat hij niet mag zien.
 *
 * Eén keuze = één model op één platform: de server geeft per rij een
 * `model_key` (platform + platform-ID). Zelfde platform-ID = één keuze (ook een
 * oude zuster-persona); FC en F2F hebben elk een eigen ID, dus een model op
 * beide platforms (Rozy, SweetSilina) staat er twee keer in, met het platform.
 */
import { useEffect, useMemo, useRef, useState } from 'react'

export interface ModelOptie { key: string; label: string; platform: string; slugs: string[] }

interface Rij { model_key?: string | null; model_slug: string | null; model_name: string | null; platform: string }

const PLAT_KORT: Record<string, string> = { fancentro: 'FC', f2f: 'F2F' }

/** Het platform uit een slug (fc_… / f2f_…), of '' als dat niet te zeggen is. */
export function platformVanSlug(slug: string): string {
  return slug.startsWith('f2f_') ? 'f2f' : slug.startsWith('fc_') ? 'fancentro' : ''
}

/** De keuzes voor deze rijen en dit platform, alfabetisch. */
export function modelOpties(rijen: Rij[], platform: string): ModelOptie[] {
  const per = new Map<string, ModelOptie>()
  for (const r of rijen) {
    const p = String(r.platform || '').toLowerCase()
    const slug = r.model_slug || ''
    if (!slug || (platform !== 'all' && p !== platform)) continue
    const key = r.model_key || `${p}:${slug}`
    const o = per.get(key) ?? { key, label: r.model_name || slug, platform: p, slugs: [] }
    if (!o.slugs.includes(slug)) o.slugs.push(slug)
    per.set(key, o)
  }
  return [...per.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) || a.platform.localeCompare(b.platform))
}

export function optieLabel(o: ModelOptie): string {
  return `${o.label} (${PLAT_KORT[o.platform] ?? o.platform})`
}

export function MmModelFilter({ opties, gekozen, onChange }: {
  opties: ModelOptie[]
  /** Gekozen slugs (uit de URL). */
  gekozen: string[]
  onChange: (slugs: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [zoek, setZoek] = useState('')
  const vak = useRef<HTMLDivElement>(null)
  const set = useMemo(() => new Set(gekozen), [gekozen])
  const aan = opties.filter((o) => o.slugs.some((s) => set.has(s)))
  useEffect(() => {
    if (!open) return
    const weg = (e: MouseEvent) => { if (vak.current && !vak.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', weg)
    return () => document.removeEventListener('mousedown', weg)
  }, [open])
  const zichtbaar = opties.filter((o) => optieLabel(o).toLowerCase().includes(zoek.trim().toLowerCase()))
  const wissel = (o: ModelOptie) => {
    const nu = o.slugs.some((s) => set.has(s))
    const rest = gekozen.filter((s) => !o.slugs.includes(s))
    onChange(nu ? rest : [...rest, ...o.slugs])
  }
  const tekst = !gekozen.length ? 'All models'
    : aan.length === 1 ? optieLabel(aan[0]) : `${aan.length || gekozen.length} models`
  return (
    <div className="mmfilter" ref={vak}>
      <button className={gekozen.length ? 'btn sm primary' : 'btn sm'} type="button" aria-label="Filter by model"
              aria-expanded={open} data-testid="modelfilter-knop" onClick={() => setOpen((v) => !v)}>
        {tekst} ▾
      </button>
      {open ? (
        <div className="mmfilter-paneel" data-testid="modelfilter-paneel">
          <input className="inp" type="search" placeholder="Search model" aria-label="Search model" autoFocus
                 value={zoek} onChange={(e) => setZoek(e.target.value)} />
          <button className="btn ghost sm" type="button" data-testid="modelfilter-alle"
                  onClick={() => { onChange([]); setOpen(false) }}>All models</button>
          <div className="mmfilter-lijst">
            {zichtbaar.length ? zichtbaar.map((o) => (
              <label className="chkrow" key={o.key} data-testid="modelfilter-optie">
                <input type="checkbox" checked={o.slugs.some((s) => set.has(s))} onChange={() => wissel(o)} />
                {optieLabel(o)}
              </label>
            )) : <div className="mut2" style={{ padding: 8 }}>No models</div>}
          </div>
        </div>
      ) : null}
    </div>
  )
}
